/** Valores por defecto de negocio (ajustar solo aquí si la marca cambia los textos). */
export const UTM_DEFAULT_SOURCE = "directo";
export const UTM_DEFAULT_MEDIUM = "landing_mkt";
export const UTM_DEFAULT_CAMPAIGN = "jeep";

export function readSessionItem(key) {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return null;
    const t = String(raw).trim();
    return t === "" ? null : t;
  } catch {
    return null;
  }
}

function normalizeParam(value) {
  if (value == null) return null;
  const t = String(value).trim();
  return t === "" ? null : t;
}

/**
 * Orígenes que no son campañas de marketing (p. ej. post-formulario viejo): se ignoran
 * para aplicar `directo` / sesión real.
 */
export function sanitizeMarketingUtmSource(value) {
  const n = normalizeParam(value);
  if (!n) return null;
  const compact = n.replace(/\s+/g, " ").replace(/_/g, " ").trim();
  if (/^consulta web$/i.test(compact)) return null;
  return n;
}

/** Medio por defecto antiguo del repo: se trata como ausente para usar `landing_mkt`. */
export function sanitizeMarketingUtmMedium(value) {
  const n = normalizeParam(value);
  if (!n) return null;
  if (n.toLowerCase() === "landing_campaña") return null;
  return n;
}

/** Sustituye en la URL query params legacy que no deben conservarse en enlaces compartidos. */
export function replaceLegacyMarketingUtmsInUrl(urlObj, utm_source, utm_medium) {
  const rawSrc = urlObj.searchParams.get("utm_source");
  if (normalizeParam(rawSrc) && sanitizeMarketingUtmSource(rawSrc) == null) {
    urlObj.searchParams.set("utm_source", utm_source || UTM_DEFAULT_SOURCE);
  }
  const rawMed = urlObj.searchParams.get("utm_medium");
  if (normalizeParam(rawMed) && sanitizeMarketingUtmMedium(rawMed) == null) {
    urlObj.searchParams.set("utm_medium", utm_medium || UTM_DEFAULT_MEDIUM);
  }
}

function getSearchString(search) {
  if (search !== undefined && search !== null) {
    if (typeof search === "string") return search;
    if (typeof search.toString === "function") return search.toString();
  }
  if (typeof window !== "undefined" && window.location && typeof window.location.search === "string") {
    return window.location.search;
  }
  return "";
}

/**
 * `true` si en la query actual faltan los tres UTMs principales:
 * `utm_source`, `utm_medium` y `utm_campaign`.
 */
export function urlSinUtmsPrincipales(search) {
  let params;
  try {
    params = new URLSearchParams(getSearchString(search));
  } catch {
    params = new URLSearchParams();
  }
  const src = sanitizeMarketingUtmSource(params.get("utm_source"));
  const med = sanitizeMarketingUtmMedium(params.get("utm_medium"));
  const camp = normalizeParam(params.get("utm_campaign"));
  return !src && !med && !camp;
}

/**
 * Resuelve utm_source y utm_medium desde la query actual (o `search` pasado), sessionStorage y defaults.
 * - Sin UTMs principales en la URL → siempre defaults (no reutiliza sesión previa).
 * - Con al menos un UTM principal en la URL → URL + sessionStorage + defaults si source/medium quedan vacíos.
 */
export function resolveUtmSourceAndMedium(search) {
  if (urlSinUtmsPrincipales(search)) {
    return {
      utm_source: UTM_DEFAULT_SOURCE,
      utm_medium: UTM_DEFAULT_MEDIUM,
    };
  }

  let params;
  try {
    params = new URLSearchParams(getSearchString(search));
  } catch {
    params = new URLSearchParams();
  }

  const fromUrl = {
    utm_source: sanitizeMarketingUtmSource(params.get("utm_source")),
    utm_medium: sanitizeMarketingUtmMedium(params.get("utm_medium")),
  };

  const sessSource = sanitizeMarketingUtmSource(readSessionItem("utm_source"));
  const sessMedium = sanitizeMarketingUtmMedium(readSessionItem("utm_medium"));

  if (!fromUrl.utm_source && !fromUrl.utm_medium) {
    return {
      utm_source: sessSource || UTM_DEFAULT_SOURCE,
      utm_medium: sessMedium || UTM_DEFAULT_MEDIUM,
    };
  }

  return {
    utm_source: fromUrl.utm_source || sessSource || UTM_DEFAULT_SOURCE,
    utm_medium: fromUrl.utm_medium || sessMedium || UTM_DEFAULT_MEDIUM,
  };
}

/**
 * utm_campaign: URL → sessionStorage → default de marca.
 * Sin UTMs principales en la URL → siempre default (no reutiliza sesión previa).
 */
export function resolveUtmCampaign(search) {
  if (urlSinUtmsPrincipales(search)) {
    return UTM_DEFAULT_CAMPAIGN;
  }

  let params;
  try {
    params = new URLSearchParams(getSearchString(search));
  } catch {
    params = new URLSearchParams();
  }

  const fromUrl = normalizeParam(params.get("utm_campaign"));
  if (fromUrl) return fromUrl;

  return readSessionItem("utm_campaign") || UTM_DEFAULT_CAMPAIGN;
}

/** @deprecated Preferir `buildUtmQueryString` desde `config/whatsappConfig`. */
export function buildUtmSourceMediumQuery(search) {
  const { utm_source, utm_medium } = resolveUtmSourceAndMedium(search);
  const utm_campaign = resolveUtmCampaign(search);
  return `?utm_source=${encodeURIComponent(utm_source)}&utm_medium=${encodeURIComponent(
    utm_medium
  )}&utm_campaign=${encodeURIComponent(utm_campaign)}`;
}
